import { GetServerSidePropsContext, PreviewData } from 'next';
import { apiIntegration } from '@lemon-io/ui-kit';
import { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ParsedUrlQuery } from 'querystring';

export type NextContext = GetServerSidePropsContext<
  ParsedUrlQuery,
  PreviewData
>;

export const { queryEndpoint, mutationEndpoint } =
  apiIntegration.createEndpointCreators<NextContext>({
    useErrorSnackbar: () => {
      const { enqueueSnackbar } = useSnackbar();

      return {
        show: () => {
          enqueueSnackbar({
            variant: 'error',
            message: 'Something went wrong: data could not be loaded',
          });
        },
      };
    },
  });

export type QueryEndpointParams<
  T extends {
    useQuery: (...args: any[]) => any;
    fetch: (...args: any[]) => any;
  }
> = T extends {
  useQuery: (
    ...args: [infer Payload, infer Options]
  ) => Record<infer _, infer Response>;
  fetch: (...args: any[]) => Promise<infer Response_1>;
}
  ? {
      payload: Payload;
      options: Options extends UseQueryOptions<infer _, any, any, any>
        ? Options
        : never;
      response: [null] extends [Response] ? Response_1 | null : Response_1;
    }
  : never;

export type MutationEndpointParams<T extends { useMutation: () => any }> =
  T extends {
    useMutation: () => { [K in string]: infer MutateFunc };
  }
    ? MutateFunc extends (
        payload: infer Payload,
        options?: infer Options
      ) => any
      ? Options extends UseMutationOptions<infer Response, any, Payload, any>
        ? { payload: Payload; options: Options; response: Response }
        : never
      : never
    : never;

export type EndpointParams<T> = T extends {
  useQuery: (payload: any, options?: any) => any;
  fetch: (...args: any[]) => any;
}
  ? QueryEndpointParams<T>
  : T extends { useMutation: () => any }
  ? MutationEndpointParams<T>
  : never;

export type EndpointsOptionsMap<T extends Record<string, any>> = {
  [K in keyof T]: EndpointParams<T[K]>;
};
